import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { colors } from '@/constants/colors';
import { toast } from 'react-toastify';
import useAuthStore from '@/store/authStore';
import { resendVerificationOTP, verifyEmail } from '@/api/auth';

const VerifyEmailBlocker = () => {
   const { user, token, setAuth, clearAuth } = useAuthStore();
   const [otp, setOtp] = useState('');
   const [sending, setSending] = useState(false);
   const [verifying, setVerifying] = useState(false);
   const [sent, setSent] = useState(false);

   // Rendering nothing is what produced the original white screen, so bail out
   // ONLY when there is no account to talk about at all. A missing id (stale
   // persisted copy from before the field existed) still gets the card — the
   // buttons explain to sign in again instead of showing an empty page.
   if (!user) return null;

   const userId = user.id;

   const handleSend = async () => {
      if (sending) return;
      if (!userId) {
         toast.error('Please sign out and sign in again to continue');
         return;
      }
      try {
         setSending(true);
         const res = await resendVerificationOTP(userId);
         toast.success(res.msg || 'Verification code sent');
         setSent(true);
      } catch (err: any) {
         toast.error(
            err?.response?.data?.err ||
               err?.response?.data?.msg ||
               'Failed to send verification code'
         );
      } finally {
         setSending(false);
      }
   };

   const handleVerify = async () => {
      if (verifying) return;
      if (!userId) {
         toast.error('Please sign out and sign in again to continue');
         return;
      }
      if (!otp.trim()) {
         toast.error('Enter the OTP we sent to your email');
         return;
      }
      try {
         setVerifying(true);
         const res = await verifyEmail({ token: otp.trim(), userId });
         if (res.status === 200) {
            const newToken = res.data?.token || token || '';
            const newUser = res.data?.user || { ...user, verified: true };
            setAuth(newToken, { ...newUser, verified: true });
            toast.success('Email verified!');
         }
      } catch (err: any) {
         toast.error(
            err?.response?.data?.err ||
               err?.response?.data?.msg ||
               'Invalid code, try again'
         );
      } finally {
         setVerifying(false);
      }
   };

   return (
      <Overlay>
         <Bg />
         <Blob $top="10%" $left="12%" />
         <Blob $top="60%" $left="70%" $lime />

         <Card>
            <IconCircle>
               <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
               </svg>
            </IconCircle>
            <Title>Verify your email</Title>
            <Desc>
               We need to confirm <Strong>{user.email}</Strong> before you can use
               SmartFi. Send yourself a 6-digit code and enter it below.
            </Desc>

            <SendBtn onClick={handleSend} disabled={sending}>
               {sending
                  ? 'Sending…'
                  : sent
                  ? 'Resend verification code'
                  : 'Send verification code'}
            </SendBtn>

            <OtpLabel>Enter OTP</OtpLabel>
            <OtpInput
               type="text"
               inputMode="numeric"
               maxLength={8}
               value={otp}
               onChange={(e) => setOtp(e.target.value.replace(/\s/g, ''))}
               placeholder="• • • • • •"
            />

            <VerifyBtn onClick={handleVerify} disabled={verifying || !otp.trim()}>
               {verifying ? 'Verifying…' : 'Verify email'}
            </VerifyBtn>

            <Footer>
               Wrong account?{' '}
               <LogoutLink onClick={() => clearAuth()}>Sign out</LogoutLink>
            </Footer>
         </Card>
      </Overlay>
   );
};

const bgShift = keyframes`
   0%, 100% { background-position: 0% 50%; }
   50% { background-position: 100% 50%; }
`;
const float = keyframes`
   0%, 100% { transform: translate(0, 0) scale(1); }
   50% { transform: translate(20px, -30px) scale(1.1); }
`;

const Overlay = styled.div`
   position: fixed;
   inset: 0;
   z-index: 2000;
   display: flex;
   align-items: center;
   justify-content: center;
   padding: 16px;
   overflow: hidden;
`;

const Bg = styled.div`
   position: absolute;
   inset: 0;
   background: linear-gradient(135deg, #0b1030 0%, #1a1f4a 35%, #2a2165 70%, #0f1535 100%);
   background-size: 200% 200%;
   animation: ${bgShift} 16s ease infinite;
`;

const Blob = styled.div<{ $top: string; $left: string; $lime?: boolean }>`
   position: absolute;
   top: ${({ $top }) => $top};
   left: ${({ $left }) => $left};
   width: 280px;
   height: 280px;
   border-radius: 50%;
   filter: blur(60px);
   background: ${({ $lime }) =>
      $lime
         ? 'radial-gradient(circle, rgba(210,253,88,0.22) 0%, rgba(210,253,88,0) 70%)'
         : 'radial-gradient(circle, rgba(55,96,249,0.35) 0%, rgba(55,96,249,0) 70%)'};
   animation: ${float} 12s ease-in-out infinite;
   pointer-events: none;
`;

const Card = styled.div`
   position: relative;
   z-index: 1;
   width: min(440px, 100%);
   padding: 28px 24px 22px;
   border-radius: 18px;
   background: linear-gradient(160deg, rgba(30, 36, 80, 0.85) 0%, rgba(20, 24, 56, 0.9) 100%);
   backdrop-filter: blur(18px);
   border: 1px solid rgba(255, 255, 255, 0.1);
   box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(55, 96, 249, 0.2);
   color: white;
   text-align: center;
`;

const IconCircle = styled.div`
   width: 56px;
   height: 56px;
   margin: 0 auto 14px;
   border-radius: 16px;
   background: linear-gradient(135deg, #3760f9 0%, #6a4dff 100%);
   display: grid;
   place-items: center;
   box-shadow: 0 8px 22px rgba(55, 96, 249, 0.45);
   svg {
      width: 28px;
      height: 28px;
   }
`;

const Title = styled.h2`
   font-size: 22px;
   font-weight: 800;
   color: white;
   margin-bottom: 8px;
`;

const Desc = styled.p`
   font-size: 13.5px;
   color: rgba(255, 255, 255, 0.7);
   line-height: 1.55;
   margin-bottom: 18px;
`;

const Strong = styled.span`
   color: #D2FD58;
   font-weight: 600;
`;

const SendBtn = styled.button<{ disabled?: boolean }>`
   width: 100%;
   padding: 11px 14px;
   border-radius: 10px;
   font-size: 13px;
   font-weight: 700;
   letter-spacing: 0.02em;
   background: rgba(255, 255, 255, 0.06);
   color: white;
   border: 1px solid rgba(255, 255, 255, 0.15);
   cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
   opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
   margin-bottom: 18px;
   &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(210, 253, 88, 0.4);
   }
`;

const OtpLabel = styled.p`
   text-align: left;
   font-size: 11px;
   font-weight: 700;
   letter-spacing: 0.1em;
   text-transform: uppercase;
   color: rgba(255, 255, 255, 0.6);
   margin-bottom: 6px;
`;

const OtpInput = styled.input`
   width: 100%;
   padding: 12px 14px;
   border-radius: 10px;
   background: rgba(255, 255, 255, 0.05);
   border: 1px solid rgba(255, 255, 255, 0.12);
   color: white;
   font-family: 'Courier New', monospace;
   font-size: 20px;
   font-weight: 700;
   letter-spacing: 0.3em;
   text-align: center;
   outline: none;
   transition: border-color 0.15s, box-shadow 0.15s;
   &:focus {
      border-color: ${colors.SECONDARY};
      box-shadow: 0 0 0 3px rgba(210, 253, 88, 0.15);
   }
   &::placeholder {
      color: rgba(255, 255, 255, 0.25);
   }
`;

const VerifyBtn = styled.button<{ disabled?: boolean }>`
   width: 100%;
   margin-top: 14px;
   padding: 12px 14px;
   border-radius: 12px;
   font-size: 14px;
   font-weight: 800;
   color: #0b1030;
   background: linear-gradient(90deg, #D2FD58 0%, #7de8ff 100%);
   border: none;
   cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
   opacity: ${({ disabled }) => (disabled ? 0.55 : 1)};
   box-shadow: 0 10px 26px rgba(210, 253, 88, 0.28);
   &:hover:not(:disabled) {
      filter: brightness(1.05);
   }
   &:active:not(:disabled) {
      transform: translateY(1px);
   }
`;

const Footer = styled.p`
   margin-top: 18px;
   font-size: 12px;
   color: rgba(255, 255, 255, 0.45);
`;

const LogoutLink = styled.span`
   color: #D2FD58;
   font-weight: 700;
   cursor: pointer;
   &:hover {
      text-decoration: underline;
   }
`;

export default VerifyEmailBlocker;
